import { getCookie, setCookie, expireAllCookies } from './setCookie';
import { DISMISSED_KEY as POLICY_NOTICE_KEY } from './policyNotice';
import safeStorage, { safeSessionStorage } from './safeStorage';

const NOTIFICATION_TOKEN_KEY = 'notification-token';
// Marks a sign-in made with "keep me logged in" off: a session cookie (no expiry), so every tab of
// the browser shares it and closing the browser ends it (PANEL-1633, BROWSER-SESSION).
const SESSION_MARKER = 'tgm_session';
// `user` held the member's whole profile (email, names, date of birth, rmsid) for a year and nothing
// read it; it is no longer written (PANEL-1628), and a copy an older release left goes with the session.
// `translator` is the same kind of unread year-long profile copy, written by profile saves.
const SESSION_COOKIES = ['auth_token', 'keepmeLogged', 'panel_langauge', 'user', 'translator', SESSION_MARKER];
// What App's boot wrote for the page being opened, which a session ended at boot must not take with
// it: the survey end page (Finished reports it with a temp token when there is no session) and an
// activation or unsubscribe link waiting for the login.
const BOOT_KEEP = [[safeStorage, 'end-page-url'], [safeSessionStorage, 'active-user-url'],
  [safeSessionStorage, 'unsubscribe-user-url']];

// The one "is someone signed in" predicate of the route table and both guards (PANEL-1633/1639): a
// non-blank token. Remember-me controls the backend session lifetime and, for a browser session, the
// boot policy below; rejected or expired sessions are cleared by the 401 interceptor.
export const isLoggedIn = () => !!(safeStorage.getItem('auth_token') || '').trim();

export const markBrowserSession = () => {
  setCookie(SESSION_MARKER, '1');
};

// The local half of every logout: the 401 interceptor, the Header and profile logout buttons,
// closing the account, changing the password or e-mail, and the 404 / redirect-to-login exits of
// the activation and unsubscribe pages. Where to go afterwards (reload, history push, goTo) stays
// with each caller.
//
// The path list is read once, before anything is expired. With no panel language cookie the
// second path is the literal "/null" (pinned by the interceptor spec); expiring a path that does
// not exist is harmless.
export const clearSession = () => {
  const paths = ['/', `/${getCookie('panel_langauge')}`, '/en', localStorage.getItem('end-page-url')];
  SESSION_COOKIES.forEach((name) => expireAllCookies(name, paths));

  // The FCM token outlives the session so the next login on this device can reuse it. Only a real
  // token is carried over: writing back getItem()'s null stored the string "null", which
  // enablePushNotification reads as a token and then never subscribes again.
  const notificationToken = localStorage.getItem(NOTIFICATION_TOKEN_KEY);
  // Dismissed policy notices (kept per member) outlive the session too: clearing them would show
  // the same banner again after every login.
  const policyNotice = localStorage.getItem(POLICY_NOTICE_KEY);
  localStorage.clear();
  sessionStorage.removeItem('active-user-url');
  sessionStorage.removeItem('unsubscribe-user-url');
  if (notificationToken && notificationToken !== 'null') {
    localStorage.setItem(NOTIFICATION_TOKEN_KEY, notificationToken);
  }
  if (policyNotice) localStorage.setItem(POLICY_NOTICE_KEY, policyNotice);
};

// Runs once at boot (containers/App), after the credential-path handling and before any route
// renders. "Keep me logged in" off ends with the browser session: a token without keepmeLogged 'true'
// and without the marker is left over from a closed browser, so the session is ended — on an e-mail
// link too, which on a shared computer would otherwise open under the previous member's session.
// What the opened page needs and the FCM token survive. A visitor without a token has nothing to end.
export const enforceSessionPolicy = () => {
  if (!isLoggedIn()) return;
  if (safeStorage.getItem('keepmeLogged') === 'true' || getCookie(SESSION_MARKER)) return;
  const kept = BOOT_KEEP.map(([storage, key]) => [storage, key, storage.getItem(key)]);
  clearSession();
  kept.forEach(([storage, key, value]) => {
    if (value !== null) storage.setItem(key, value);
  });
};
